import { useTranslation } from "react-i18next"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useError } from "../../context/useErrorContext"
import { useLoad } from "../../context/useLoadContext"
import { GetRequest, PutRequest } from "../../services/ApiClientService"
import type { IWhatsAppAiMessageSettings } from "../../Objects/WhatsAppAutomation/WhatsAppAiMessageSettings"

type ApiWrappedResponse<T> = {
  success?: boolean
  message?: string
  data?: T
}

export const defaultWhatsAppAiMessageSettings: IWhatsAppAiMessageSettings = {
  isEnabled: false,
  isOrderStatusEnabled: true,
  isRemarketingEnabled: false,
  isAwardEnabled: true,
  isPromotionEnabled: false,
  isBotEnabled: false,
  aiEndpointUrl: "https://api.openai.com/v1/chat/completions",
  aiApiKey: "",
  aiModel: "gpt-4o-mini",
  botDisplayName: "Atendente virtual",
  botPersonality:
    "Atenda como uma pessoa real do restaurante: com educacao, clareza, naturalidade e mensagens curtas. Use somente informacoes do restaurante, cardapio, promocoes, cupons e avisos sincronizados pelo sistema. Nao invente precos, prazos, taxas, produtos ou descontos. Quando faltar informacao, diga que vai chamar um atendente humano.",
  canGuideOrders: true,
  humanHandoffKeywords:
    "atendente, humano, falar com pessoa, gerente, reclamacao, reclamar, problema, cancelar, suporte",
  humanHandoffTimeoutMinutes: 10,
  remarketingIntervalDays: 15,
  orderStatusTemplate:
    "Olá, {{customerName}}! Seu pedido #{{orderId}} no {{restaurantName}} agora está: {{orderStatus}}.",
  remarketingTemplate:
    "Olá, {{customerName}}! Sentimos sua falta no {{restaurantName}}. Faça um novo pedido pelo link: {{publicMenuLink}}.",
  awardTemplate: "Parabéns, {{customerName}}! Você ganhou {{awardName}} no {{restaurantName}}. Acesse: {{awardLink}}.",
  promotionTemplate:
    "Olá, {{customerName}}! O {{restaurantName}} tem uma promoção para você: {{promotionName}}. Veja: {{promotionLink}}.",
}

export const useWhatsAppAiMessageSettings = () => {
  const { t } = useTranslation("general")

  const { enableLoad, disableLoad } = useLoad()

  const { setError } = useError()

  const resolveErrorMessage = (message?: string) => {
    const trimmed = message?.trim()

    return trimmed && trimmed.length > 0 ? trimmed : t("label_unknown_error_description")
  }

  function unwrapResponseData<T>(response: unknown, fallbackValue: T): T {
    const wrapped = response as ApiWrappedResponse<T>

    if (typeof response === "object" && response !== null && "success" in response) {
      if (wrapped.success === false) {
        throw new Error(resolveErrorMessage(wrapped.message))
      }

      return (wrapped.data ?? fallbackValue) as T
    }

    return (response ?? fallbackValue) as T
  }

  const getSettings = async (): Promise<IWhatsAppAiMessageSettings> => {
    enableLoad()

    try {
      const response = await GetRequest<IWhatsAppAiMessageSettings>(API_ROUTES.WHATSAPP_AUTOMATION.AI_MESSAGE_SETTINGS)

      return { ...defaultWhatsAppAiMessageSettings, ...unwrapResponseData(response, defaultWhatsAppAiMessageSettings) }
    } catch (e: unknown) {
      const message = e instanceof Error ? resolveErrorMessage(e.message) : t("label_unknown_error_description")

      setError(t("label_title_erro"), message)

      return defaultWhatsAppAiMessageSettings
    } finally {
      disableLoad()
    }
  }

  const saveSettings = async (payload: IWhatsAppAiMessageSettings): Promise<IWhatsAppAiMessageSettings> => {
    enableLoad()

    try {
      const response = await PutRequest<IWhatsAppAiMessageSettings>(
        API_ROUTES.WHATSAPP_AUTOMATION.AI_MESSAGE_SETTINGS,
        payload,
      )
      return unwrapResponseData(response, payload)
    } catch (e: unknown) {
      const message = e instanceof Error ? resolveErrorMessage(e.message) : t("label_unknown_error_description")
      setError(t("label_title_erro"), message)
      throw e
    } finally {
      disableLoad()
    }
  }

  return { getSettings, saveSettings }
}
